import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaCompass,
  FaTimes,
  FaFire,
  FaUsers,
  FaQuestionCircle,
  FaChartLine,
  FaChevronUp,
} from "react-icons/fa";
import "./MobileWidgetDrawer.css";

// Import existing widgets
import TrendingTopicsWidget from "./widgets/TrendingTopicsWidget";
import PeopleSuggestionsWidget from "./widgets/PeopleSuggestionsWidget";
import UnansweredQuestions from "./widgets/UnansweredQuestions";
import BountyQuestions from "./widgets/BountyQuestions";
import OnlineFriendsWidget from "./widgets/OnlineFriendsWidget";
import UserStatsWidget from "./widgets/UserStatsWidget";

const MobileWidgetDrawer = ({ widgets }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("trending");
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef(null);
  const startYRef = useRef(0);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle touch/drag to close
  const handleTouchStart = (e) => {
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      setIsOpen(false);
    }
    setDragY(0);
  };

  const tabs = [
    { id: "trending", icon: FaFire, label: "Trending" },
    { id: "people", icon: FaUsers, label: "People" },
    { id: "questions", icon: FaQuestionCircle, label: "Q&A" },
    { id: "stats", icon: FaChartLine, label: "Stats" },
  ];

  const renderActiveWidget = () => {
    switch (activeTab) {
      case "trending":
        return (
          <div className="mwd-widget-container">
            <TrendingTopicsWidget topics={widgets?.trendingTopics || []} />
          </div>
        );
      case "people":
        return (
          <div className="mwd-widget-container">
            <PeopleSuggestionsWidget suggestions={widgets?.suggestions || []} />
            <OnlineFriendsWidget friends={widgets?.onlineFriends || []} />
          </div>
        );
      case "questions":
        return (
          <div className="mwd-widget-container">
            <BountyQuestions />
            <UnansweredQuestions />
          </div>
        );
      case "stats":
        return (
          <div className="mwd-widget-container">
            {widgets?.userStats && (
              <UserStatsWidget stats={widgets.userStats} />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Floating Discover Button */}
      <button
        className={`mwd-fab ${isOpen ? "hidden" : ""}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open Discover"
      >
        <FaCompass className="mwd-fab-icon" />
        <span className="mwd-fab-label">Discover</span>
      </button>

      {/* Bottom Sheet Drawer */}
      {isOpen &&
        createPortal(
          <div className="mwd-overlay" onClick={() => setIsOpen(false)}>
            <div
              ref={drawerRef}
              className={`mwd-drawer ${isDragging ? "dragging" : ""}`}
              style={{
                transform: `translateY(${dragY}px)`,
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Drag Handle */}
              <div className="mwd-drag-handle">
                <div className="mwd-handle-bar"></div>
              </div>

              {/* Header */}
              <div className="mwd-header">
                <h2 className="mwd-title">
                  <FaCompass /> Discover
                </h2>
                <button
                  className="mwd-close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="mwd-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`mwd-tab ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <tab.icon className="mwd-tab-icon" />
                    <span className="mwd-tab-label">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Widget Content */}
              <div className="mwd-content">{renderActiveWidget()}</div>

              {/* Swipe hint */}
              <div className="mwd-swipe-hint">
                <FaChevronUp />
                <span>Swipe down to close</span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MobileWidgetDrawer;
